import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Lock, Check } from "@phosphor-icons/react";
import { changePassword, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const { lang } = useI18n();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm(""); setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) {
      setError(lang === "el" ? "Ο νέος κωδικός χρειάζεται τουλάχιστον 6 χαρακτήρες" : "New password must be at least 6 characters");
      return;
    }
    if (next !== confirm) {
      setError(lang === "el" ? "Οι κωδικοί δεν ταιριάζουν" : "Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await changePassword(current, next);
      toast.success(lang === "el" ? "Ο κωδικός άλλαξε" : "Password changed");
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-white border-line rounded-2xl max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Lock size={18} weight="duotone" className="text-forest" />
            {lang === "el" ? "Αλλαγή κωδικού" : "Change password"}
          </DialogTitle>
          <DialogDescription className="text-sm text-ink-soft">
            {lang === "el"
              ? "Δώσε τον τρέχοντα κωδικό και διάλεξε έναν νέο."
              : "Enter your current password and choose a new one."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="kicker-label mb-2 block">{lang === "el" ? "Τρέχων κωδικός" : "Current password"}</label>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              className="cs-input w-full"
              data-testid="cp-current"
              autoFocus
            />
          </div>
          <div>
            <label className="kicker-label mb-2 block">{lang === "el" ? "Νέος κωδικός" : "New password"}</label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={6}
              className="cs-input w-full"
              data-testid="cp-new"
              placeholder={lang === "el" ? "Τουλάχιστον 6 χαρακτήρες" : "At least 6 characters"}
            />
          </div>
          <div>
            <label className="kicker-label mb-2 block">{lang === "el" ? "Επιβεβαίωση" : "Confirm"}</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              className="cs-input w-full"
              data-testid="cp-confirm"
            />
          </div>

          {error && (
            <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2" data-testid="cp-error">
              {error}
            </div>
          )}

          <div className="flex justify-between gap-2 pt-2">
            <button type="button" onClick={() => { reset(); onOpenChange(false); }}
              className="px-5 py-2.5 rounded-full text-sm text-ink-soft hover:text-ink" data-testid="cp-cancel">
              {lang === "el" ? "Ακύρωση" : "Cancel"}
            </button>
            <button type="submit" disabled={loading}
              className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-sm bg-forest text-cream hover:bg-forest-dark font-medium disabled:opacity-60" data-testid="cp-submit">
              <Check size={14} weight="bold" />
              {loading ? "…" : (lang === "el" ? "Αλλαγή" : "Change")}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
